<template>
  <div class="employer-form-wrapper">

    <div class="page-header text-center">
      <h1 class="page-title">
        {{ t('employerForm.title') }}
      </h1>

      <p class="page-subtitle">
        {{ t('employerForm.subtitle') }}
      </p>
    </div>
    <div class="form-top-section">
      <div class="notice-block">
        <div class="notice_text fw-semibold text-dark mb-2">
          {{ t('common.form_important_notice_text') }}
        </div>
        <div class="notice-title fw-bold text-dark text-dark mb-2">
          {{ t('common.the_form_is_for_both_men_and_women') }}
        </div>
      </div>
    </div>
    <form @submit.prevent="handleSubmit" class="form-card">

      <div class="space-y-8">
        <div class="container_wrap">
          <EmployerDetails
            v-model="formData.employer"
            :formErrors="formErrors"
            @removeError="removeError"
            @setError = "setError"
          />

          <CandidateDetails
            v-model="formData.candidate"
            :formErrors="formErrors"
            :countries="cgCountries"
            @removeError="removeError"
          />

          <ProviderSelection
            v-model="formData.providerSelection"
            :formErrors="formErrors"
            @removeError="removeError"
          />

          <InsuranceCalculation
            v-model="formData.insuranceCalculation"
            :provider="formData.providerSelection?.provider"
            :insurnacePeriod="formData.candidate?.insurancePeriod"
            :settings="settings"
          />
          <InfoForCandidate
            v-model="formData.termInfoForCandidate"
            :formErrors="formErrors"
            @removeError="removeError"
          />
          <DeclarationsSection
            v-model="formData.declarations"
            :formErrors="formErrors"
            @removeError="removeError"
          />

          <CentralizedInsuranceViewConsent
            v-model="formData.centralizedInsuranceViewConsent"
            :formErrors="formErrors"
            @removeError="removeError"
          />

          <ConfirmationsSection
            v-model="formData.confirmation"
            :formErrors="formErrors"
            @removeError="removeError"
          />

          <EmployerSignature
            v-model="formData.signature"
            :formErrors="formErrors"
            @removeError="removeError"
          />

        </div>

      </div>

      <div class="form-actions">
        <button type="submit" class="btn-primary">
          {{ t('common.continue') }}
        </button>
      </div>

    </form>

  </div>
</template>

<script setup>
import { computed, nextTick, onMounted, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { merge } from 'lodash';
import { useRoute, useRouter } from 'vue-router';
import { http } from '@/services/http';
import Error from '@/utils/Error';
import { useToast } from 'vue-toastification';

/* Sections */
import EmployerDetails from '@/components/employerForm/EmployerDetails.vue';
import CandidateDetails from '@/components/employerForm/CandidateDetails.vue';
import ProviderSelection from '@/components/employerForm/ProviderSelection.vue';
import InsuranceCalculation from '@/components/employerForm/InsuranceCalculation.vue';
import DeclarationsSection from '@/components/employerForm/DeclarationsSection.vue';
import CentralizedInsuranceViewConsent from '@/components/employerForm/CentralizedInsuranceViewConsent.vue';
import ConfirmationsSection from '@/components/employerForm/ConfirmationsSection.vue';
import EmployerSignature from '@/components/employerForm/EmployerSignature.vue';
import InfoForCandidate from '@/components/employerForm/InfoForCandidate.vue';
import { useStore } from 'vuex';

const { t } = useI18n();
const router = useRouter();
const route = useRoute();
const toast = useToast();
const today = new Date();
const formattedToday = today.toISOString().split('T')[0];
const store = useStore();
const expiryDate = new Date(
  today.getTime() + ((365 - 1) * 24 * 60 * 60 * 1000)
);
const formattedExpiry = expiryDate.toISOString().split('T')[0];

/* Default Form */
const getDefaultEmployerForm = () => ({
  employer: {
    agentName:"Cabiri Misgav insurance agency",
    agentNumber:"400019",
    preferredCommunication:"email"
  },
  candidate: {
    gender:"",
    address: {},
    insurancePeriod: {
      from:formattedToday,
      to:formattedExpiry
    },
    purpose:'nursing'
  },
  providerSelection: {},
  insuranceCalculation: {},
  termInfoForCandidate:{
    disabilityLawTermAccepted: false,
    nintyDaysTermsAccepted: false,
  },
  declarations: {
    policyholderDeclarationAccepted: false,
    candidateDeclarationAccepted: false,
    beneficiariesConsentAccepted: false,
    agentAuthorizationAccepted: false,
    materialInformationAccepted: false,
    digitalCommunicationAccepted:true,
    privacyPolicyAccepted:false,
  },
  centralizedInsuranceViewConsent: {
    accepted: false
  },
  confirmation: {},
  signature: {},
});

/* State */
const formData = reactive(getDefaultEmployerForm());
const formErrors = reactive(new Error({}));
const cgCountries = ref({});

/* Load existing data */
onMounted(async () => {
  await store.dispatch('setLocale','he');
  await store.dispatch('ensureInsuranceSettings');
  await fetchDropdowns();
  await fetchEmployerForm();
});

const settings = computed(() => {
  return store.getters.settings
})
const fetchDropdowns = async () => {
  try {
    const { data } = await http.get('fetch-dropdowns');
    cgCountries.value = data.countries;
    console.log(cgCountries.value);
    
  } catch (error) {
    toast.error('Failed to load dropdowns');
  }
}
const fetchEmployerForm = async () => {
  try {
    const { data } = await http.get('fetch-insurance-data');
    const isFromIsavta = route.query.from === 'isavta';
    if (isFromIsavta) {
      if(data?.current_step == "health_questionnaire"){
        return router.replace({ name: 'HealthInsurance' });
      }
      if(data?.current_step == "payment"){
        return router.replace({ name: 'PaymentMethodPage' });
      }
    }
    if(data?.current_step == "completed"){
      return router.replace({ name: 'ThankYouPage' });
    }
    if (data) {
      merge(formData, getDefaultEmployerForm(), data.params?.employer_form || {});
    }
  } catch (e) {
    console.log(e);
    
    toast.error('Failed to load form');
  }
};

/* Submit */
const handleSubmit = async () => {
  try {
    await http.post('save-employer-form', formData);
    toast.success(t("employerForm.toast.submit_success"));
    router.push({ name: 'HealthInsurance' });

    formErrors.clear?.();

  } catch (error) {
    if (error?.data?.errors) {
      Object.assign(formErrors, new Error(error.data.errors));
      await nextTick();
      const firstError = document.querySelector('.is-invalid');
      if (firstError) {
        firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    } else {
      toast.error('Something went wrong');
    }
  }
};

/* Error handler */
const removeError = (name) => {
  formErrors.remove(name);
};
const setError = ({ key, message }) => {
  formErrors.set(key,message);
};
</script>

<style scoped>
.employer-form-wrapper {
  width: 1200px;
  margin: 0 auto;
}

.page-header {
  margin-bottom: 24px;
}

.page-title {
  font-size: 30px;
  font-weight: 600;
}

.page-subtitle {
  font-size: 26px;
  color: #6b7280;
}

.form-card {
  background: #fff;
  padding: 32px;
  border-radius: 14px;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 32px;
}

.btn-primary {
  background: #2563eb;
  color: #fff;
  padding: 12px 24px;
  border-radius: 10px;
}

.agent-block {
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
  padding: 16px 20px;
  background: #f8f9fa;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  margin-bottom: 24px;
}

.agent-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.agent-item h6 {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: #333;
}

.agent-item span {
  font-size: 14px;
  color: #555;
}
.notice-text {
  font-size: 12px;
}

.notice-title {
  font-size: 16px;
}
</style>